import React from "react";
import { NewValueParams, ValueGetterParams, ValueSetterParams } from "ag-grid-community";
import { AgGridReact, toast, type AgGrid } from "@jsq/ds/core";
import { Colors, TextMD, TextLG } from "@jsq/ds/styles";
import {
  type SetDataVaultExtensionFieldValuePayload,
  type ExtensionFieldValueObjectType,
  type ExtensionFieldObjectType,
} from "~/graphql/supergraphTypes";
import { toDbId } from "~/utils/graphql-helpers";
import { SectionWithFields, TabWithSections } from "../../../CustomFields/types";
import { EXTENSION_FIELD_TYPE_IDS, serializeFieldValue } from "../../utils/extensionFieldUtils";
import { getCellEditorForFieldType, getColumnFilterForFieldType } from "./cellEditors/types";

export const isColDef = (
  colDef: AgGrid.ColDef | AgGrid.ColGroupDef | undefined | null,
): colDef is AgGrid.ColDef => {
  return colDef != null && "field" in colDef;
};

// Extract field value for editing from AG Grid cell value
const extractFieldValueForEditing = (value: unknown, fieldType: number): string => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (typeof value === "object" && value !== null) {
    const objectValue = value as ExtensionFieldValueObjectType;

    switch (fieldType) {
      case EXTENSION_FIELD_TYPE_IDS.INTEGER:
        return objectValue.intValue?.toString() || "";
      case EXTENSION_FIELD_TYPE_IDS.FLOAT:
      case EXTENSION_FIELD_TYPE_IDS.CURRENCY_USD:
        return objectValue.floatValue?.toString() || "";
      case EXTENSION_FIELD_TYPE_IDS.TEXT:
        return objectValue.stringValue || "";
      case EXTENSION_FIELD_TYPE_IDS.ENUMERATION:
        return objectValue.enumValue?.enumValueCode || objectValue.stringValue || "";
      case EXTENSION_FIELD_TYPE_IDS.DATE:
        if (objectValue.dateValue) {
          try {
            const date = new Date(objectValue.dateValue);
            return date.toISOString().split("T")[0];
          } catch {
            return objectValue.stringValue || "";
          }
        }
        return objectValue.stringValue || "";
      case EXTENSION_FIELD_TYPE_IDS.PERCENTILE:
        if (objectValue.floatValue !== undefined && objectValue.floatValue !== null) {
          return objectValue.floatValue.toString();
        }
        return "";
      default:
        return objectValue.stringValue || "";
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  return String(value);
};
const createUpdatedExtensionFieldValue = (
  currentValue: ExtensionFieldValueObjectType,
  serializedValue: string,
  fieldType: number,
  mutationResult?: ExtensionFieldValueObjectType | null,
): ExtensionFieldValueObjectType => {
  if (mutationResult) {
    return {
      ...currentValue,
      ...mutationResult,
      extensionField: currentValue?.extensionField || mutationResult.extensionField,
    };
  }

  return createValueByFieldType(currentValue, serializedValue, fieldType);
};

export const createValueByFieldType = (
  currentValue: ExtensionFieldValueObjectType,
  serializedValue: string,
  fieldType: number,
): ExtensionFieldValueObjectType => {
  const baseValue = { ...currentValue };

  switch (fieldType) {
    case EXTENSION_FIELD_TYPE_IDS.TEXT:
      return { ...baseValue, stringValue: serializedValue };
    case EXTENSION_FIELD_TYPE_IDS.INTEGER: {
      if (!serializedValue.trim()) {
        return { ...baseValue, intValue: null };
      }
      const intValue = parseInt(serializedValue, 10);
      return { ...baseValue, intValue: isNaN(intValue) ? null : intValue };
    }
    case EXTENSION_FIELD_TYPE_IDS.FLOAT:
    case EXTENSION_FIELD_TYPE_IDS.PERCENTILE:
    case EXTENSION_FIELD_TYPE_IDS.CURRENCY_USD: {
      if (!serializedValue.trim()) {
        return { ...baseValue, floatValue: null };
      }
      const floatValue = parseFloat(serializedValue);
      return { ...baseValue, floatValue: isNaN(floatValue) ? null : floatValue };
    }
    case EXTENSION_FIELD_TYPE_IDS.DATE:
      return { ...baseValue, dateValue: serializedValue, stringValue: serializedValue };
    case EXTENSION_FIELD_TYPE_IDS.ENUMERATION:
      return createEnumValue(baseValue, serializedValue);
    default:
      return { ...baseValue, stringValue: serializedValue };
  }
};

const createEnumValue = (
  currentValue: ExtensionFieldValueObjectType,
  serializedValue: string,
): ExtensionFieldValueObjectType => {
  if (!serializedValue) {
    return {
      ...currentValue,
      enumValue: null,
      stringValue: "",
    };
  }

  try {
    const enumValue = JSON.parse(serializedValue);
    return {
      ...currentValue,
      enumValue: enumValue,
      stringValue: enumValue?.enumValueLabel || "",
    };
  } catch {
    return {
      ...currentValue,
      stringValue: serializedValue,
      enumValue:
        currentValue.enumValue?.enumValueCode === serializedValue
          ? currentValue.enumValue
          : null,
    };
  }
};

// Update extension field data and local state
const updateExtensionFieldData = async (
  params: NewValueParams,
  fieldId: string,
  fieldType: number,
  serializedValue: string,
  updateExtensionFieldValue: (params: {
    entityId: number;
    extensionFieldId: string;
    value: string;
  }) => Promise<unknown>,
  setLocalEdits: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, ExtensionFieldValueObjectType>>>
  >,
): Promise<void> => {
  const { data } = params;

  if (!data.id) {
    throw new Error("Entity ID is missing from row data");
  }

  // Use existing utility to convert ID - handles both Global ID and string formats
  const entityId =
    typeof data.id === "string" && data.id.includes(":")
      ? toDbId(data.id)
      : parseInt(String(data.id), 10);

  if (isNaN(entityId)) {
    throw new Error(`Invalid entity ID format: ${data.id}`);
  }

  const mutationResult = await updateExtensionFieldValue({
    entityId: entityId,
    extensionFieldId: fieldId,
    value: serializedValue,
  });

  if (params.node && params.data && params.data.extensionFieldValues) {
    const currentExtensionFieldValue = params.data.extensionFieldValues[fieldId];
    const mutationExtensionFieldValue = (
      mutationResult as SetDataVaultExtensionFieldValuePayload | null | undefined
    )?.extensionFieldValue;

    const updatedExtensionFieldValue = createUpdatedExtensionFieldValue(
      currentExtensionFieldValue,
      serializedValue,
      fieldType,
      mutationExtensionFieldValue,
    );

    // Update local edits state
    setLocalEdits((prevEdits) => ({
      ...prevEdits,
      [data.id]: {
        ...prevEdits[data.id],
        [fieldId]: updatedExtensionFieldValue,
      },
    }));

    // Update grid data
    params.data.extensionFieldValues = {
      ...params.data.extensionFieldValues,
      [fieldId]: updatedExtensionFieldValue,
    };
  }
};

const refreshGridCell = (params: NewValueParams): void => {
  if (!params.api || !params.node) {
    return;
  }

  if (typeof params.api.applyTransaction === "function") {
    params.api.applyTransaction({
      update: [params.data],
    });
  } else {
    params.api.refreshCells({
      rowNodes: [params.node],
      columns: [params.colDef.field!],
      force: true,
    });
  }
};

// Handle cell value changes for extension fields
export const createCellValueChangedHandler = (
  updateExtensionFieldValue: (params: {
    entityId: number;
    extensionFieldId: string;
    value: string;
  }) => Promise<unknown>,
  setLocalEdits: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, ExtensionFieldValueObjectType>>>
  >,
) => {
  return (params: NewValueParams) => {
    const { colDef, newValue, oldValue } = params;

    const fieldId = colDef.cellEditorParams?.fieldId || colDef.cellRendererParams?.fieldId;
    const fieldType =
      colDef.cellEditorParams?.fieldType || colDef.cellRendererParams?.fieldType;

    // Use utility functions for value extraction
    const actualOldValue = extractFieldValueForEditing(oldValue, fieldType);
    const actualNewValue = extractFieldValueForEditing(newValue, fieldType);

    // Early return if no change detected
    if (actualOldValue === actualNewValue) {
      return;
    }

    void (async () => {
      try {
        if (!fieldId || !fieldType) {
          throw new Error("Missing field information in column definition");
        }

        const serializedValue = serializeFieldValue(actualNewValue, fieldType);

        // Update extension field data using utility function
        await updateExtensionFieldData(
          params,
          fieldId,
          fieldType,
          serializedValue,
          updateExtensionFieldValue,
          setLocalEdits,
        );

        // Refresh grid display
        refreshGridCell(params);
      } catch (error) {
        // Revert to old value and refresh grid
        if (params.node && params.data && params.data.extensionFieldValues) {
          params.data.extensionFieldValues[fieldId] = oldValue;
          refreshGridCell(params);
        }

        // Show user-friendly error message
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update field value";
        toast.error(`Update failed: ${errorMessage}`);
      }
    })();
  };
};

// Generate AG Grid column definitions for extension fields
export const generateExtensionFieldColumns = (
  extensionFields: ExtensionFieldObjectType[],
  onCellValueChanged?: (params: NewValueParams) => void,
): AgGridReact.AgGridReactProps["columnDefs"] => {
  if (!extensionFields || extensionFields.length === 0) {
    return [];
  }

  const supportedExtensionFields = extensionFields.filter(
    (field) => field.extensionFieldTypeId !== 5,
  );

  return supportedExtensionFields.map((field) => {
    const isEditable = !!onCellValueChanged;
    const cellEditorName = getCellEditorForFieldType(field.extensionFieldTypeId);
    const columnFilterName = getColumnFilterForFieldType(field.extensionFieldTypeId);

    const baseColumn: AgGrid.ColDef = {
      field: `extensionField_${field.id}`,
      headerName: field.name,
      sortable: false,
      minWidth: 120,
      maxWidth: 400, // Allow expansion up to 400px
      suppressSizeToFit: true, // Prevent auto-sizing from grid width constraint
      resizable: true, // Allow manual resizing
      autoHeight: true, // Allow row height to expand for wrapped text
      cellRenderer: "ExtensionFieldCellRenderer",
      cellRendererParams: {
        fieldType: field.extensionFieldTypeId,
        enumValues: field.enumValues || [],
        fieldId: field.id,
      },
      editable: isEditable,
      menuTabs: ["generalMenuTab"],
    };

    const editableProps = isEditable
      ? {
          cellEditor: cellEditorName,
          cellEditorParams: {
            fieldType: field.extensionFieldTypeId,
            enumValues: field.enumValues || [],
            fieldId: field.id,
            field: field,
          },
          singleClickEdit: true,
        }
      : {};

    return {
      ...baseColumn,
      ...editableProps,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        fontWeight: "400",
        fontSize: "14px",
        color: Colors.Neutral600,
        backgroundColor: Colors.Neutral100,
        cursor: onCellValueChanged ? "pointer" : "default",
        whiteSpace: "normal",
        wordWrap: "break-word",
        overflowWrap: "break-word",
        lineHeight: "1.4",
      },
      headerClass: "extension-field-header",
      colId: `extensionField_${field.id}`,
      filter: columnFilterName,
      filterParams:
        columnFilterName === "agDateColumnFilter"
          ? {
              maxNumConditions: 1,
              buttons: ["reset"],
              filterOptions: ["equals", "inRange", "lessThan", "greaterThan"],
              defaultOption: "equals",
            }
          : columnFilterName === "agSetColumnFilter"
            ? {
                values: (field.enumValues || []).map((ev) => ev.enumValueLabel),
                defaultToNothingSelected: true,
                maxNumConditions: 1,
                buttons: ["reset"],
              }
            : {
                maxNumConditions: 1,
                buttons: ["reset"],
              },
      suppressRowClickSelection: onCellValueChanged,
      valueGetter: (params: ValueGetterParams) => {
        const extensionFieldValues = params.data?.extensionFieldValues;

        if (!extensionFieldValues || !extensionFieldValues[field.id]) {
          return null;
        }

        const extensionFieldValue = extensionFieldValues[field.id];

        // Ensure we always return a valid ExtensionFieldValueObjectType object
        // The cell renderer expects this structure for all field types
        if (!extensionFieldValue || typeof extensionFieldValue !== "object") {
          return null;
        }

        return extensionFieldValue;
      },
      valueSetter: (params: ValueSetterParams) => {
        if (params.data && params.data.extensionFieldValues) {
          const currentExtensionFieldValue = params.data.extensionFieldValues[field.id];
          let updatedExtensionFieldValue;

          switch (field.extensionFieldTypeId) {
            case EXTENSION_FIELD_TYPE_IDS.TEXT:
              updatedExtensionFieldValue = {
                ...currentExtensionFieldValue,
                stringValue: params.newValue,
              };
              break;
            case EXTENSION_FIELD_TYPE_IDS.ENUMERATION: {
              // For enumeration fields, find the enum object by code
              const selectedEnum = (field.enumValues || []).find(
                (enumValue) => enumValue.enumValueCode === params.newValue,
              );
              updatedExtensionFieldValue = {
                ...currentExtensionFieldValue,
                stringValue: params.newValue,
                enumValue: selectedEnum || null,
              };
              break;
            }
            case EXTENSION_FIELD_TYPE_IDS.INTEGER: {
              const trimmedValue = params.newValue?.trim() || "";
              let intValue = null;
              if (trimmedValue) {
                const parsed = parseInt(trimmedValue, 10);
                intValue = isNaN(parsed) ? null : parsed;
              }
              updatedExtensionFieldValue = {
                ...currentExtensionFieldValue,
                intValue,
              };
              break;
            }
            case EXTENSION_FIELD_TYPE_IDS.FLOAT:
            case EXTENSION_FIELD_TYPE_IDS.CURRENCY_USD:
            case EXTENSION_FIELD_TYPE_IDS.PERCENTILE: {
              const trimmedValue = params.newValue?.trim() || "";
              let floatValue = null;
              if (trimmedValue) {
                const parsed = parseFloat(trimmedValue);
                floatValue = isNaN(parsed) ? null : parsed;
              }
              updatedExtensionFieldValue = {
                ...currentExtensionFieldValue,
                floatValue,
              };
              break;
            }
            case EXTENSION_FIELD_TYPE_IDS.DATE:
              updatedExtensionFieldValue = {
                ...currentExtensionFieldValue,
                stringValue: params.newValue,
                dateValue: params.newValue,
              };
              break;
            default:
              updatedExtensionFieldValue = {
                ...currentExtensionFieldValue,
                stringValue: params.newValue,
              };
          }

          params.data.extensionFieldValues[field.id] = updatedExtensionFieldValue;
        }

        return true;
      },
    };
  });
};

// Create column group for extension fields
export const createExtensionFieldColumnGroup = (
  extensionFieldColumns: AgGridReact.AgGridReactProps["columnDefs"],
): {
  headerName: string;
  headerClass: string;
  children: AgGridReact.AgGridReactProps["columnDefs"];
} | null => {
  if (!extensionFieldColumns || extensionFieldColumns.length === 0) {
    return null;
  }

  return {
    headerName: "Custom fields",
    headerClass: "extension-fields-group-header",
    children: extensionFieldColumns,
  };
};

/**
 * Creates section-based column groups using SectionWithFields data structure
 * This avoids the need to pass extensionFields and sections separately
 */
export const createSectionBasedColumnGroups = (
  extensionFieldColumns: AgGridReact.AgGridReactProps["columnDefs"],
  sectionsWithFields: SectionWithFields[],
): Array<{
  headerName: string;
  headerClass: string;
  children: AgGridReact.AgGridReactProps["columnDefs"];
}> => {
  if (!extensionFieldColumns || extensionFieldColumns.length === 0) {
    return [];
  }

  // Create a map of field ID to column for easy lookup
  const fieldIdToColumn = new Map<string, AgGrid.ColDef>();
  extensionFieldColumns.forEach((column) => {
    if (isColDef(column) && column.field) {
      const fieldId = column.field.replace("extensionField_", "");
      fieldIdToColumn.set(fieldId, column);
    }
  });

  const sectionGroups: Array<{
    headerName: string;
    headerClass: string;
    children: AgGridReact.AgGridReactProps["columnDefs"];
  }> = [];

  // Create groups for each section
  sectionsWithFields.forEach((sectionWithFields) => {
    const sectionColumns: AgGridReact.AgGridReactProps["columnDefs"] = [];

    sectionWithFields.fields.forEach((field) => {
      const column = fieldIdToColumn.get(field.id);
      if (column) {
        sectionColumns.push(column);
      }
    });

    if (sectionColumns.length > 0) {
      sectionGroups.push({
        headerName: sectionWithFields.section.name,
        headerClass: `extension-fields-section-header ${TextMD}`,
        children: sectionColumns,
      });
    }
  });

  return sectionGroups;
};

/**
 * Creates tab-based column groups using TabWithSections data structure
 * This reuses createSectionBasedColumnGroups for each tab to avoid code duplication
 */
export const createTabBasedColumnGroups = (
  extensionFieldColumns: AgGridReact.AgGridReactProps["columnDefs"],
  tabsWithSections: TabWithSections[],
): Array<{
  headerName: string;
  headerClass: string;
  children: AgGridReact.AgGridReactProps["columnDefs"];
}> => {
  if (!extensionFieldColumns || extensionFieldColumns.length === 0) {
    return [];
  }

  const tabColumnGroups: Array<{
    headerName: string;
    headerClass: string;
    children: AgGridReact.AgGridReactProps["columnDefs"];
  }> = [];

  // Iterate through tabs and create section groups for each tab
  tabsWithSections.forEach((tab) => {
    const sectionGroups = createSectionBasedColumnGroups(extensionFieldColumns, tab.sections);

    if (sectionGroups.length > 0) {
      tabColumnGroups.push({
        headerName: tab.tabName,
        headerClass: `${TextLG} extension-fields-tab-header`,
        children: sectionGroups,
      });
    }
  });

  return tabColumnGroups;
};
